import {
  Badge,
  Card,
  Loader,
  Stack,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Search } from "lucide-react";

import { PageHeader } from "@/components/PageHeader";
import { api } from "@/lib/api/client";
import { formatDate } from "@/lib/format";

interface AdminExamRow {
  id: string;
  clinic_id: string;
  clinic_name: string;
  exam_number: number;
  type: string;
  status: string;
  final_verdict: string | null;
  job_title: string | null;
  patient_name: string;
  patient_card: string;
  created_at: string;
}

export function AdminExamsPage() {
  const [search, setSearch] = useState("");
  const [debSearch] = useDebouncedValue(search, 250);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-all-exams"],
    queryFn: async () => {
      const { data } = await api.get<AdminExamRow[]>("/admin/examinations");
      return data;
    },
  });

  const filtered = (data ?? []).filter((e) => {
    if (!debSearch.trim()) return true;
    const q = debSearch.toLowerCase();
    return (
      e.patient_name.toLowerCase().includes(q) ||
      e.patient_card.toLowerCase().includes(q) ||
      e.clinic_name.toLowerCase().includes(q) ||
      (e.job_title ?? "").toLowerCase().includes(q) ||
      e.status.toLowerCase().includes(q) ||
      e.type.toLowerCase().includes(q)
    );
  });

  return (
    <Stack gap="lg">
      <PageHeader
        title="Barcha ko'riklar"
        subtitle={`${filtered.length} ko'rik`}
      />

      <Card padding="md">
        <TextInput
          rightSection={<Search size={18} strokeWidth={2} />}
          placeholder="Bemor, klinika, ish joyi, holat..."
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
        />
      </Card>

      <Card padding={0} radius="xl">
        <Table.ScrollContainer minWidth={1100}>
          <Table verticalSpacing="md" striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Klinika</Table.Th>
                <Table.Th>#</Table.Th>
                <Table.Th>Bemor</Table.Th>
                <Table.Th>Ish joyi</Table.Th>
                <Table.Th>Turi</Table.Th>
                <Table.Th>Holat</Table.Th>
                <Table.Th>Xulosa</Table.Th>
                <Table.Th>Sana</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {isLoading && (
                <Table.Tr><Table.Td colSpan={8}><Loader size="sm" /></Table.Td></Table.Tr>
              )}
              {filtered.map((e) => (
                <Table.Tr key={e.id}>
                  <Table.Td>
                    <Badge variant="light" color="brand" radius="sm">
                      {e.clinic_name}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text ff="monospace" fw={700} c="brand.7" size="sm">#{e.exam_number}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Stack gap={0}>
                      <Text size="sm" fw={600}>{e.patient_name}</Text>
                      <Text size="xs" c="dimmed" ff="monospace">{e.patient_card}</Text>
                    </Stack>
                  </Table.Td>
                  <Table.Td><Text size="sm">{e.job_title ?? "—"}</Text></Table.Td>
                  <Table.Td><Badge variant="light" radius="sm">{e.type}</Badge></Table.Td>
                  <Table.Td>
                    <Badge
                      variant="light"
                      color={
                        e.status === "completed" ? "green"
                          : e.status === "cancelled" ? "red"
                          : e.status === "pending_chief" ? "orange" : "blue"
                      }
                      radius="sm"
                    >
                      {e.status}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    {e.final_verdict ? (
                      <Badge variant="filled" color="green" radius="sm">{e.final_verdict}</Badge>
                    ) : <Text size="xs" c="dimmed">—</Text>}
                  </Table.Td>
                  <Table.Td><Text size="xs" c="dimmed">{formatDate(e.created_at)}</Text></Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Card>
    </Stack>
  );
}
